/**
 * VIAFIDO Portrait Customizer
 * 
 * Handles aggregator product → reference product flow with product spoofing.
 * 
 * Wizard Structure:
 * - Step 1: Number of Pets (from aggregator) + Upload fields
 * - Step 2: Remaining aggregator options + Reference product options + Buy button
 */

(function () {
  'use strict';

  const API_ENDPOINT = 'https://app.suite.viafido.com/api/products';

  class ViafidoCustomizer {
    constructor(config) {
      this.config = {
        aggregatorProductId: config.aggregatorProductId,
        sectionId: config.sectionId,
        formId: config.formId,
        pickerSettings: config.pickerSettings || {},
        ...config
      };

      this.state = {
        aggregatorVariant: null,
        referenceProductId: null,
        referenceProduct: null,
        referenceVariant: null,
        referenceProductsCache: {},
        selectedOptions: {},
        selectedAggregatorOptions: {},
        uploads: [],
        isLoading: false,
        apiLoaded: false,
        currentStep: 1
      };

      this.elements = {};
      this.init();
    }

    async init() {
      this.cacheElements();
      this.hideNativeVariantPicker();
      this.createWizardStructure();
      this.bindEvents();

      // Fetch all reference products upfront
      await this.loadAllReferenceProducts();
    }

    cacheElements() {
      this.elements.form = document.getElementById(this.config.formId);
      this.elements.variantInput = this.elements.form?.querySelector('input[name="id"]');
      this.elements.nativePicker = document.querySelector(`#variant-picker-${this.config.sectionId}`);
      this.elements.pickerContainer = document.querySelector('.hdt-product__variant-picker');
      this.elements.buyButton = document.querySelector('.hdt-product-form__submit');
      this.elements.buyButtonContainer = document.querySelector('.hdt-product__buy-button');
    }

    /**
     * Hide native theme variant picker - we'll render our own
     */
    hideNativeVariantPicker() {
      if (this.elements.nativePicker) {
        this.elements.nativePicker.style.display = 'none';
      }
    }

    /**
     * Hide existing wizard controls - we'll rebuild them
     */
    hideExistingWizardControls() {
      const existingControls = document.querySelector('.wizard-controls');
      if (existingControls) {
        existingControls.style.display = 'none';
      }
      const existingSteps = document.querySelector('.wizard-steps');
      if (existingSteps) {
        existingSteps.style.display = 'none';
      }
    }

    /**
     * Create custom wizard structure with proper step ordering
     */
    createWizardStructure() {
      // Create main wizard container
      const wizardContainer = document.createElement('div');
      wizardContainer.id = 'viafido-wizard';
      wizardContainer.className = 'viafido-wizard';

      // Step 1: Number of Pets + Uploads
      const step1 = document.createElement('div');
      step1.className = 'viafido-wizard-step';
      step1.dataset.step = '1';
      step1.innerHTML = `
        <div class="viafido-step-content">
          <div class="viafido-num-pets-picker"></div>
          <div class="viafido-upload-container"></div>
        </div>
      `;

      // Step 2: Remaining aggregator options + Reference product options
      const step2 = document.createElement('div');
      step2.className = 'viafido-wizard-step';
      step2.dataset.step = '2';
      step2.innerHTML = `
        <div class="viafido-step-content">
          <div class="viafido-aggregator-options"></div>
          <div class="viafido-reference-picker-container"></div>
        </div>
      `;

      wizardContainer.appendChild(step1);
      wizardContainer.appendChild(step2);

      // Insert before the picker container
      if (this.elements.pickerContainer) {
        this.elements.pickerContainer.insertAdjacentElement('afterend', wizardContainer);
      }

      // Cache wizard elements
      this.elements.wizardContainer = wizardContainer;
      this.elements.step1 = step1;
      this.elements.step2 = step2;
      this.elements.numPetsContainer = step1.querySelector('.viafido-num-pets-picker');
      this.elements.uploadContainer = step1.querySelector('.viafido-upload-container');
      this.elements.aggregatorOptionsContainer = step2.querySelector('.viafido-aggregator-options');
      this.elements.referencePickerContainer = step2.querySelector('.viafido-reference-picker-container');

      // Create wizard controls
      this.createWizardControls();

      // Initially show step 1
      this.setActiveStep(1);
    }

    /**
     * Create wizard navigation controls (prev, step indicators, next)
     */
    createWizardControls() {
      const controlsContainer = document.createElement('div');
      controlsContainer.className = 'viafido-wizard-controls';

      // Prev button
      const prevParent = document.createElement('div');
      prevParent.className = 'viafido-wizard-btn-parent viafido-prev-parent';
      const prevBtn = this.createThemedButton('←');
      prevBtn.className += ' viafido-wizard-btn viafido-wizard-prev';
      prevBtn.addEventListener('click', () => this.prevStep());
      prevParent.appendChild(prevBtn);

      // Step indicators
      const stepsContainer = document.createElement('div');
      stepsContainer.className = 'viafido-wizard-steps';
      for (let i = 1; i <= 2; i++) {
        const step = document.createElement('div');
        step.className = 'viafido-wizard-step-indicator';
        step.textContent = i;
        step.dataset.stepNum = i;
        step.addEventListener('click', () => {
          if (i === 1 || this.canProceedToNextStep()) {
            this.setActiveStep(i);
          }
        });
        stepsContainer.appendChild(step);
      }

      // Next button
      const nextParent = document.createElement('div');
      nextParent.className = 'viafido-wizard-btn-parent viafido-next-parent';
      const nextBtn = this.createThemedButton('Continue to Customize →');
      nextBtn.className += ' viafido-wizard-btn viafido-wizard-next';
      nextBtn.addEventListener('click', () => this.nextStep());
      nextParent.appendChild(nextBtn);

      controlsContainer.appendChild(prevParent);
      controlsContainer.appendChild(stepsContainer);
      controlsContainer.appendChild(nextParent);

      // Insert after wizard container
      this.elements.wizardContainer.appendChild(controlsContainer);

      // Cache control elements
      this.elements.wizardControls = controlsContainer;
      this.elements.prevBtn = prevBtn;
      this.elements.nextBtn = nextBtn;
      this.elements.stepIndicators = stepsContainer.querySelectorAll('.viafido-wizard-step-indicator');
    }

    createThemedButton(text) {
      const button = document.createElement('button');
      button.className = 'hdt-product-form__submit hdt-btn hdt-oh hdt-btn-solid hdt-font-semibold hdt-relative hdt-truncate hdt-btn-loading__svg custom-box-shadow';
      button.style.backgroundColor = 'rgb(var(--color-accent))';
      button.style.color = 'rgb(var(--color-accent-secondary))';
      button.style.borderColor = 'rgb(var(--color-accent-secondary))';
      button.style.borderWidth = '.2rem';
      button.type = 'button';
      button.innerHTML = `<span class="hdt-btn-atc_text">${text}</span>`;
      return button;
    }

    /**
     * Extract and render aggregator options
     */
    renderAggregatorOptions() {
      const nativePicker = this.elements.nativePicker;
      if (!nativePicker) return;

      const fieldsets = nativePicker.querySelectorAll('fieldset');

      fieldsets.forEach((fieldset, fieldsetIndex) => {
        const legend = fieldset.querySelector('legend');
        const optionName = legend?.textContent?.split(':')[0]?.trim() || '';
        const optionNameLower = optionName.toLowerCase();

        // Clone the fieldset
        const clonedFieldset = fieldset.cloneNode(true);
        clonedFieldset.classList.add('viafido-fieldset');

        // Fix duplicate IDs - generate unique IDs for cloned inputs/labels
        clonedFieldset.querySelectorAll('input[is-value]').forEach((input, inputIndex) => {
          const newId = `viafido-agg-${fieldsetIndex}-${inputIndex}`;
          const oldId = input.id;
          input.id = newId;

          // Update corresponding label's for attribute
          const label = clonedFieldset.querySelector(`label[for="${oldId}"]`);
          if (label) {
            label.setAttribute('for', newId);
          }
        });

        // Add change listeners to cloned inputs
        clonedFieldset.querySelectorAll('input[is-value]').forEach(input => {
          input.addEventListener('change', () => {
            this.handleAggregatorOptionChange(optionName, input.value);
          });
        });

        if (optionNameLower === 'number of pets') {
          // Step 1: Number of Pets
          this.elements.numPetsContainer.appendChild(clonedFieldset);

          // Listen for changes to update upload zones
          clonedFieldset.querySelectorAll('input[is-value]').forEach(input => {
            input.addEventListener('change', () => {
              this.updateUploadZones(this.extractPetCount(input.value));
            });
          });

          // Initialize with current selection
          const checkedInput = clonedFieldset.querySelector('input[is-value]:checked');
          if (checkedInput) {
            this.state.selectedAggregatorOptions[optionName] = checkedInput.value;
            this.updateUploadZones(this.extractPetCount(checkedInput.value));
          }
        } else {
          // Step 2: Other aggregator options (Frame Style, Art Style, etc.)
          this.elements.aggregatorOptionsContainer.appendChild(clonedFieldset);

          // Initialize with current selection
          const checkedInput = clonedFieldset.querySelector('input[is-value]:checked');
          if (checkedInput) {
            this.state.selectedAggregatorOptions[optionName] = checkedInput.value;
          }
        }
      });
    }

    handleAggregatorOptionChange(optionName, value) {
      this.state.selectedAggregatorOptions[optionName] = value;

      // Update legend text
      const container = optionName.toLowerCase() === 'number of pets'
        ? this.elements.numPetsContainer
        : this.elements.aggregatorOptionsContainer;

      const legend = container.querySelector(`fieldset [update-value]`);
      if (legend) {
        // Find the correct legend for this option
        container.querySelectorAll('fieldset').forEach(fieldset => {
          const fieldLegend = fieldset.querySelector('legend');
          if (fieldLegend?.textContent?.includes(optionName)) {
            const updateValue = fieldset.querySelector('[update-value]');
            if (updateValue) updateValue.textContent = value;
          }
        });
      }

      // Find matching aggregator variant
      this.findAndApplyAggregatorVariant();
    }

    findAndApplyAggregatorVariant() {
      // Get the selected values from our cloned fieldsets
      const selectedOptions = this.state.selectedAggregatorOptions;

      // Find the variant ID from the mapping
      const aggregatorData = window.viafidoAggregatorData || {};
      const variantMap = aggregatorData.variantReferenceMap || {};
      const productOptions = aggregatorData.productOptions || [];
      const variants = aggregatorData.variants || [];

      // Match selected options to find variant
      const matchedVariant = variants.find(v => {
        return Object.entries(selectedOptions).every(([name, value]) => {
          const optionIndex = productOptions.findIndex(o => o.toLowerCase() === name.toLowerCase());
          if (optionIndex === -1) return true;
          return v[`option${optionIndex + 1}`] === value;
        });
      });

      if (matchedVariant) {
        const referenceProductId = variantMap[matchedVariant.id];

        if (referenceProductId && referenceProductId !== this.state.referenceProductId) {
          this.state.referenceProductId = referenceProductId;
          this.loadReferenceProductFromCache(referenceProductId);

          // Update Media Gallery using Section Rendering API
          if (this.state.referenceProduct) {
            this.updateMediaGallery(this.state.referenceProduct);
          }
        }
      } else {
        console.warn('ViafidoCustomizer: No matching variant found');
      }
    }

    /**
     * Update product media gallery using Section Rendering API
     */
    async updateMediaGallery(product) {
      if (!product || !product.handle) return;

      const sectionId = this.config.sectionId;
      const url = `/products/${product.handle}?section_id=${sectionId}`;

      try {
        const response = await fetch(url);
        const text = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const newMediaWrapper = doc.querySelector('.hdt-product-media-wrapp');
        const currentMediaWrapper = document.querySelector('.hdt-product-media-wrapp');

        if (newMediaWrapper && currentMediaWrapper) {
          currentMediaWrapper.innerHTML = newMediaWrapper.innerHTML;
          if (this.state.referenceVariant) {
            setTimeout(() => {
              this.triggerProductMediaSlide(this.state.referenceVariant);
            }, 100);
          }
        }
      } catch (error) {
        console.error('Customizer: Failed to update media gallery', error);
      }
    }

    bindEvents() {
      // We rely on native form submission now, but we need to ensure properties are synced
      // when the form is submitted, just in case.
      if (this.elements.form) {
        this.elements.form.addEventListener('submit', () => {
          this.syncPropertiesToForm();
        });
      }
    }

    // ... (rest of methods) ...

    // ========== Cart Override ==========

    // ========== Cart Properties Sync ==========

    syncPropertiesToForm() {
      if (!this.elements.form) return;

      // 1. Clear existing hidden property inputs created by us
      this.elements.form.querySelectorAll('input.viafido-hidden-property').forEach(el => el.remove());

      const properties = this.getUploadMetadata();

      // Add artist choice property if they selected "Let artist decide" for background
      if (this.state.artistChoosesBackground) {
        properties['Background Color'] = 'Artist will choose best one';
      }

      // 2. Create new hidden inputs
      Object.entries(properties).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${key}]`;
        input.value = value;
        input.classList.add('viafido-hidden-property');
        this.elements.form.appendChild(input);
      });
    }

    /**
     * Wizard Navigation
     */
    setActiveStep(step) {
      this.state.currentStep = step;

      // Update step visibility
      this.elements.step1.classList.toggle('viafido-step-active', step === 1);
      this.elements.step1.classList.toggle('viafido-step-inactive', step !== 1);
      this.elements.step2.classList.toggle('viafido-step-active', step === 2);
      this.elements.step2.classList.toggle('viafido-step-inactive', step !== 2);

      // Update our custom step indicators
      this.elements.stepIndicators?.forEach(indicator => {
        const stepNum = parseInt(indicator.dataset.stepNum, 10);
        indicator.classList.toggle('active', stepNum === step);
      });

      // Update prev button (hidden on step 1)
      if (this.elements.prevBtn) {
        this.elements.prevBtn.style.display = step === 1 ? 'none' : '';
        this.elements.prevBtn.parentElement.style.flex = step === 1 ? '0' : '';
      }

      // Update next button (hidden on step 2, disabled if uploads incomplete)
      if (this.elements.nextBtn) {
        if (step === 2) {
          this.elements.nextBtn.style.display = 'none';
          this.elements.nextBtn.parentElement.style.flex = '0';
        } else {
          const uploadsComplete = this.areUploadsComplete();
          this.elements.nextBtn.style.display = '';
          this.elements.nextBtn.parentElement.style.flex = '1';
          this.elements.nextBtn.disabled = !uploadsComplete;
          this.elements.nextBtn.classList.toggle('hidden', false); // Always show the button
          // Update button text based on upload status
          this.elements.nextBtn.textContent = uploadsComplete ? 'Continue to Customize →' : 'Upload to Continue';
        }
      }

      // Show/hide buy button based on step
      if (this.elements.buyButtonContainer) {
        this.elements.buyButtonContainer.style.display = step === 2 ? '' : 'none';
      }

      // Scroll to top
      const productTitle = document.querySelector('.hdt-product__title');
      if (productTitle) {
        productTitle.scrollIntoView({ behavior: 'smooth' });
      }

      // Ensure button state is correct when entering step
      this.updateBuyButton(this.state.referenceVariant);
    }

    canProceedToNextStep() {
      if (this.state.currentStep === 1) {
        // Step 1: Must have all uploads complete
        return this.areUploadsComplete();
      }
      return true;
    }

    nextStep() {
      if (this.canProceedToNextStep() && this.state.currentStep < 2) {
        this.setActiveStep(this.state.currentStep + 1);
      }
    }

    prevStep() {
      if (this.state.currentStep > 1) {
        this.setActiveStep(this.state.currentStep - 1);
      }
    }

    /**
     * Fetch ALL reference products upfront
     */
    async loadAllReferenceProducts() {
      this.state.isLoading = true;
      this.showLoading();

      try {
        const response = await fetch(`${API_ENDPOINT}?id=${this.config.aggregatorProductId}`);
        const json = await response.json();

        if (json.data) {
          Object.keys(json.data).forEach(productId => {
            this.state.referenceProductsCache[productId] = this.normalizeApiProduct(json.data[productId]);
          });
          this.state.apiLoaded = true;
          console.log('ViafidoCustomizer: Loaded', Object.keys(this.state.referenceProductsCache).length, 'reference products');

          // Now render aggregator options (after API loaded)
          this.renderAggregatorOptions();

          // Handle initial selection
          this.findAndApplyAggregatorVariant();
        }
      } catch (error) {
        console.error('ViafidoCustomizer: Failed to load reference products', error);
      } finally {
        this.state.isLoading = false;
        this.hideLoading();
      }
    }

    loadReferenceProductFromCache(productId) {
      const product = this.state.referenceProductsCache[productId];

      if (product) {
        this.state.referenceProduct = product;
        this.renderReferenceProductPicker();
        this.autoSelectFirstVariant();
      } else {
        console.warn('ViafidoCustomizer: Reference product not found in cache:', productId);
      }
    }

    normalizeApiProduct(apiProduct) {
      const variants = apiProduct.variants || [];

      const optionsMap = new Map();
      const images = apiProduct.images || [];
      const urlToIdMap = new Map();
      const filenameToIdMap = new Map(); // Fallback map

      const getFilename = (url) => {
        if (!url) return '';
        return url.split('?')[0].split('/').pop();
      };

      images.forEach(img => {
        if (img.url) {
          urlToIdMap.set(img.url, img.id);
          urlToIdMap.set(img.url.split('?')[0], img.id);
          filenameToIdMap.set(getFilename(img.url), img.id);
        }
      });

      variants.forEach(variant => {
        (variant.selectedOptions || []).forEach((opt, index) => {
          if (!optionsMap.has(opt.name)) {
            optionsMap.set(opt.name, { name: opt.name, position: index + 1, values: new Set() });
          }
          optionsMap.get(opt.name).values.add(opt.value);
        });
      });

      const options_with_values = Array.from(optionsMap.values()).map(opt => ({
        name: opt.name,
        position: opt.position,
        values: Array.from(opt.values)
      }));

      return {
        id: apiProduct.id,
        title: apiProduct.title,
        handle: apiProduct.handle,
        options_with_values,
        variants: variants.map(v => ({
          id: v.id,
          title: v.title,
          price: parseFloat(v.price?.amount || 0) * 100,
          compare_at_price: v.compareAtPrice ? parseFloat(v.compareAtPrice.amount) * 100 : null,
          available: true,
          option1: v.selectedOptions?.[0]?.value,
          option2: v.selectedOptions?.[1]?.value,
          option3: v.selectedOptions?.[2]?.value,
          featured_image: v.image?.url ? { src: v.image.url } : null,
          featured_media: (() => {
            if (!v.image?.url) return null;
            if (v.image.id) {
              const idStr = String(v.image.id);
              const numericId = idStr.includes('/') ? idStr.split('/').pop() : idStr;
              return { id: numericId };
            }

            const cleanUrl = v.image.url.split('?')[0];
            let mediaId = urlToIdMap.get(cleanUrl) || urlToIdMap.get(v.image.url);

            if (!mediaId) {
              const filename = getFilename(v.image.url);
              mediaId = filenameToIdMap.get(filename);
            }

            if (!mediaId) {
              console.warn('Customizer: Could not find media ID for variant image:', v.title, v.image.url);
            }
            return { id: mediaId };
          })(),
          metafields: {
            custom: {
              background_color: v.background_color,
              frame_style: v.frame_style,
              frame_size: v.frame_size,
              artwork_style_thumb: v.artwork_style_thumb,
              theme_thumb: v.theme_thumb,
              number_of_pets_thumb: v.number_of_pets_thumb
            }
          }
        }))
      };
    }

    /**
     * Render reference product variant picker into Step 2
     */
    renderReferenceProductPicker() {
      const product = this.state.referenceProduct;
      if (!product) return;

      const container = this.elements.referencePickerContainer;
      if (!container) return;

      // Clear existing
      container.innerHTML = '';

      // Build picker HTML
      const pickerHTML = this.buildVariantPickerHTML(product);
      container.innerHTML = pickerHTML;

      this.bindReferencePickerEvents(container);
    }

    buildVariantPickerHTML(product) {
      const settings = this.config.pickerSettings;
      const colorPickerType = settings.color_picker_type || 'custom_thumbnail';
      const colorSize = settings.color_size || 'large';
      const pickerType = settings.picker_type || 'block';

      let html = '';

      product.options_with_values.forEach((option, optionIndex) => {
        const optionName = option.name.toLowerCase();
        const optionType = this.getOptionType(option.name);
        const fieldsetType = optionType === 'color' ? colorPickerType : pickerType;

        // Special handling for Background Color - add toggle
        if (optionName === 'background color' || optionName === 'background') {
          html += this.buildBackgroundColorWithToggle(product, option, optionIndex);
          return;
        }

        html += `
          <fieldset class="hdt-product-form__input is-style-${optionType} viafido-fieldset" type="${fieldsetType}" data-index="${optionIndex}">
            <legend class="hdt-product-form__label hdt-s-text" style="font-size: 1.7rem;">
              <span>${option.name}: <span class="hdt-font-semibold" update-value>${option.values[0]}</span></span>
            </legend>
            <div class="hdt-product-form__values">
        `;

        option.values.forEach((value, valueIndex) => {
          html += this.buildOptionValueHTML(product, option, value, optionIndex, valueIndex);
        });

        html += `
            </div>
          </fieldset>
        `;
      });

      return html;
    }

    /**
     * Build Background Color with "Let artist decide" / "I'll choose" toggle
     */
    buildBackgroundColorWithToggle(product, option, optionIndex) {
      const firstValue = option.values[0];

      let html = `
        <fieldset class="hdt-product-form__input is-style-color viafido-fieldset viafido-bg-color-fieldset" data-index="${optionIndex}">
          <legend class="hdt-product-form__label hdt-s-text" style="font-size: 1.7rem;">
            <span>${option.name}: <span class="hdt-font-semibold" update-value>Artist's Choice</span></span>
          </legend>
          
          <!-- Toggle Tabs -->
          <div class="viafido-bg-toggle">
            <button type="button" class="viafido-bg-toggle-btn active" data-choice="artist">
              Let the artist decide
            </button>
            <button type="button" class="viafido-bg-toggle-btn" data-choice="custom">
              I'll choose myself
            </button>
          </div>
          
          <!-- Hidden input for "artist decides" option -->
          <input 
            type="hidden" 
            name="viafido-ref-${this.escapeHtml(option.name)}" 
            value="${this.escapeHtml(firstValue)}"
            data-viafido-ref-option
            data-option-name="${this.escapeHtml(option.name)}"
            data-viafido-bg-hidden
          >
          
          <!-- Color options (hidden by default) -->
          <div class="hdt-product-form__values viafido-bg-options" style="display: none;">
      `;

      option.values.forEach((value, valueIndex) => {
        html += this.buildOptionValueHTML(product, option, value, optionIndex, valueIndex);
      });

      html += `
          </div>
        </fieldset>
      `;

      return html;
    }

    buildOptionValueHTML(product, option, value, optionIndex, valueIndex) {
      const optionName = option.name.toLowerCase();
      const inputId = `viafido-ref-${this.config.sectionId}-${option.position}-${valueIndex}`;
      const isFirstValue = valueIndex === 0;

      // Size options - always render as plain buttons (no thumbnails)
      const isSizeOption = optionName === 'size' || optionName === 'frame size';
      const thumbUrl = isSizeOption ? null : this.getOptionThumbnail(product, option.name, value);
      const styleConfig = this.getOptionStyle(optionName);

      let html = `
        <input 
          autocomplete="off" 
          is-value 
          type="radio" 
          id="${inputId}" 
          name="viafido-ref-${this.escapeHtml(option.name)}" 
          value="${this.escapeHtml(value)}" 
          form="${this.config.formId}"
          ${isFirstValue ? 'checked' : ''}
          class="sr-only"
          data-viafido-ref-option
          data-option-name="${this.escapeHtml(option.name)}"
        >
      `;

      if (thumbUrl) {
        html += `
          <label 
            is="hdt-label-tooltip" 
            for="${inputId}"
            style="${styleConfig.labelStyle}"
            class="hdt-product-form_value hdt-product-form_value_has-image is-type-color custom-box-shadow"
          >
            <img 
              src="${thumbUrl}" 
              srcset="${thumbUrl}"
              loading="lazy" 
              style="border-radius: 15.5px"
              alt="${this.escapeHtml(value)}"
            >
            <span class="sr-only">${this.escapeHtml(value)}</span>
            <span class="hdt-form-color-name sr-only">${this.escapeHtml(value)}</span>
          </label>
        `;
      } else {
        // Plain button style for Size and fallback
        html += `
          <label 
            for="${inputId}"
            class="hdt-product-form_value is-type-block"
          >
            <span class="sr-only"></span>
            ${this.escapeHtml(value)}
          </label>
        `;
      }

      return html;
    }

    getOptionType(optionName) {
      const name = optionName.toLowerCase();
      const colorList = ['color', 'colour', 'background color', 'background'];
      const sizeList = ['size', 'frame size'];

      if (colorList.some(c => name.includes(c))) return 'color';
      if (sizeList.some(s => name.includes(s))) return 'size';
      return 'other';
    }

    getOptionStyle(optionName) {
      const styles = {
        'frame size': { labelStyle: 'margin-bottom: 1.5%; width: 32.3%; height: auto; border-radius: 17px; padding: 0px !important; border-width: .2rem;' },
        'frame style': { labelStyle: 'margin-bottom: 1.5%; width: 49.25%; height: auto; border-radius: 17px; padding: 0px !important; border-width: .2rem;' },
        'background color': { labelStyle: 'margin-bottom: 1.5%; width: 32.3%; height: auto; border-radius: 17px; padding: 0px !important; border-width: .2rem;' },
        'size': { labelStyle: 'margin-bottom: 1.5%; width: 32.3%; height: auto; border-radius: 17px; padding: 0px !important; border-width: .2rem;' },
        default: { labelStyle: 'margin-bottom: 1.5%; width: 22.5%; height: auto; border-radius: 15px; padding: 0px !important; border-width: .2rem;' }
      };
      return styles[optionName] || styles.default;
    }

    getOptionThumbnail(product, optionName, value) {
      const variant = product.variants.find(v => {
        return v.option1 === value || v.option2 === value || v.option3 === value;
      });

      if (!variant) return null;

      const name = optionName.toLowerCase();
      const metafields = variant.metafields?.custom || {};

      if (name === 'background color' || name.includes('background')) return metafields.background_color;
      if (name === 'frame style') return metafields.frame_style;
      if (name === 'frame size') return metafields.frame_size;
      if (name === 'artwork method') return metafields.artwork_style_thumb;
      if (name === 'theme') return metafields.theme_thumb;
      if (name === 'number of pets') return metafields.number_of_pets_thumb;

      return variant.featured_image?.src || null;
    }

    bindReferencePickerEvents(container) {
      // Bind radio input changes
      container.querySelectorAll('input[data-viafido-ref-option]').forEach(input => {
        input.addEventListener('change', () => {
          this.handleReferenceOptionChange();
        });
      });

      // Bind background color toggle
      this.bindBackgroundColorToggle(container);
    }

    /**
     * Bind background color toggle (Let artist decide / I'll choose)
     */
    bindBackgroundColorToggle(container) {
      const bgFieldset = container.querySelector('.viafido-bg-color-fieldset');
      if (!bgFieldset) return;

      const toggleBtns = bgFieldset.querySelectorAll('.viafido-bg-toggle-btn');
      const bgOptions = bgFieldset.querySelector('.viafido-bg-options');
      const hiddenInput = bgFieldset.querySelector('[data-viafido-bg-hidden]');
      const legendValue = bgFieldset.querySelector('[update-value]');

      // Initialize state - artist choice by default
      this.state.artistChoosesBackground = true;

      toggleBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const choice = btn.dataset.choice;
          const isArtistChoice = choice === 'artist';

          // Update active state
          toggleBtns.forEach(b => b.classList.remove('active'));
          btn.classList.add('active');

          // Show/hide color options
          if (bgOptions) {
            bgOptions.style.display = isArtistChoice ? 'none' : 'flex';
          }

          // Update state
          this.state.artistChoosesBackground = isArtistChoice;

          if (isArtistChoice) {
            // "Let artist decide" - set Pearl internally, update legend
            if (hiddenInput) {
              hiddenInput.value = 'Pearl';
              hiddenInput.disabled = false;
            }
            if (legendValue) {
              legendValue.textContent = "Artist's Choice";
            }
            // Disable radio inputs so hidden input takes precedence
            bgOptions?.querySelectorAll('input[type="radio"]').forEach(r => r.disabled = true);
          } else {
            // "I'll choose" - enable radio inputs, disable hidden
            if (hiddenInput) {
              hiddenInput.disabled = true;
            }
            bgOptions?.querySelectorAll('input[type="radio"]').forEach(r => r.disabled = false);
            // Select first option by default
            const firstRadio = bgOptions?.querySelector('input[type="radio"]');
            if (firstRadio && !bgOptions.querySelector('input[type="radio"]:checked')) {
              firstRadio.checked = true;
              if (legendValue) legendValue.textContent = firstRadio.value;
            }
          }

          // Trigger variant update
          this.handleReferenceOptionChange();
          this.syncPropertiesToForm();
        });
      });
    }

    handleReferenceOptionChange() {
      const container = this.elements.referencePickerContainer;
      if (!container) return;

      const selectedOptions = {};

      // Collect from checked radio inputs
      container.querySelectorAll('input[data-viafido-ref-option]:checked:not(:disabled)').forEach(input => {
        const optionName = input.dataset.optionName;
        selectedOptions[optionName] = input.value;
      });

      // Collect from hidden inputs (for Background Color when artist decides)
      container.querySelectorAll('input[data-viafido-bg-hidden]:not(:disabled)').forEach(input => {
        const optionName = input.dataset.optionName;
        selectedOptions[optionName] = input.value;
      });

      this.state.selectedOptions = selectedOptions;

      // Update legend text (but skip Background Color when artist decides)
      container.querySelectorAll('fieldset').forEach(fieldset => {
        // Skip Background Color fieldset if artist choice is active
        if (fieldset.classList.contains('viafido-bg-color-fieldset') && this.state.artistChoosesBackground) {
          return;
        }

        const checkedInput = fieldset.querySelector('input[data-viafido-ref-option]:checked:not(:disabled)');
        if (checkedInput) {
          const legend = fieldset.querySelector('[update-value]');
          if (legend) legend.textContent = checkedInput.value;
        }
      });

      const variant = this.findMatchingVariant(selectedOptions);
      if (variant) {
        this.spoofVariant(variant);
      }
      this.syncPropertiesToForm();
    }

    findMatchingVariant(selectedOptions) {
      const product = this.state.referenceProduct;
      if (!product) return null;

      return product.variants.find(v => {
        const optionEntries = Object.entries(selectedOptions);
        return optionEntries.every(([name, value]) => {
          const optionIndex = product.options_with_values.findIndex(o => o.name === name);
          if (optionIndex === -1) return true;
          return v[`option${optionIndex + 1}`] === value;
        });
      });
    }

    autoSelectFirstVariant() {
      const product = this.state.referenceProduct;
      if (!product || !product.variants.length) return;

      const firstVariant = product.variants[0];
      this.spoofVariant(firstVariant);
    }

    spoofVariant(variant) {
      this.state.referenceVariant = variant;

      if (this.elements.variantInput) {
        this.elements.variantInput.value = variant.id;
        this.elements.variantInput.disabled = false;
      }

      this.dispatchVariantChange(variant);
      this.updatePriceDisplay(variant);
      this.updateBuyButton(variant);
      this.triggerProductMediaSlide(variant);
    }

    triggerProductMediaSlide(variant) {
      if (!variant) return;

      let mediaId = null;
      const variantSlide = document.querySelector(`.hdt-slider__slide[data-variants-id*="${variant.id}"]`);

      if (variantSlide) {
        mediaId = variantSlide.getAttribute('data-media-id');
      }

      // Strategy 2: Fallback to API data
      if (!mediaId && variant.featured_media && variant.featured_media.id) {
        mediaId = variant.featured_media.id;
      }

      if (mediaId) {
        const thumb = document.querySelector(`[data-media-id="${mediaId}"].hdt-thumbnail__media, .hdt-product-media__thumb [data-media-id="${mediaId}"]`);

        if (thumb) {
          thumb.click();
          return; // Success
        }

        // Fallback Action: Direct Slider API control (if thumbnail missing or hidden)
        const mainSlider = document.querySelector('.hdt-product-media__main hdt-slider');
        if (mainSlider && mainSlider.scrollToTarget) {
          const targetSlide = mainSlider.querySelector(`.hdt-slider__slide[data-media-id="${mediaId}"]`);
          if (targetSlide) {
            mainSlider.scrollToTarget(targetSlide);
            return;
          }
        }
      }
    }

    dispatchVariantChange(variant) {
      if (this.elements.form) {
        const event = new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant, sectionId: this.config.sectionId }
        });
        this.elements.form.dispatchEvent(event);

        if (this.elements.variantInput) {
          this.elements.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    updatePriceDisplay(variant) {
      const formattedPrice = this.formatMoney(variant.price);
      const formattedCompareAt = variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : null;

      document.querySelectorAll(`hdt-price[form="${this.config.formId}"] .hdt-money`).forEach(el => {
        el.textContent = formattedPrice;
        // Clear cached currency data so theme can reconvert
        delete el.dataset.baseCurrency;
        delete el.dataset.hdtCurrency;
      });

      document.querySelectorAll(`hdt-compare-at-price[form="${this.config.formId}"]`).forEach(el => {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          el.hidden = false;
          const priceEl = el.querySelector('.hdt-money');
          if (priceEl) {
            priceEl.textContent = formattedCompareAt;
            delete priceEl.dataset.baseCurrency;
            delete priceEl.dataset.hdtCurrency;
          }
        } else {
          el.hidden = true;
        }
      });

      const qtyPrice = document.querySelector(`hdt-qty-price[form="${this.config.formId}"] .hdt-money`);
      if (qtyPrice) {
        qtyPrice.textContent = formattedPrice;
        delete qtyPrice.dataset.baseCurrency;
        delete qtyPrice.dataset.hdtCurrency;
      }

      // Trigger theme's currency conversion to localize prices
      document.dispatchEvent(new CustomEvent('currency:update', { bubbles: true }));
    }

    updateBuyButton(variant) {
      if (!this.elements.buyButton) return;

      const canAddToCart = variant && variant.available && this.areUploadsComplete();
      this.elements.buyButton.disabled = !canAddToCart;

      const textEl = this.elements.buyButton.querySelector('.hdt-btn-atc_text');
      if (textEl && variant?.available) {
        textEl.textContent = 'Add to Cart';
      }
    }

    formatMoney(cents) {
      // Try Shopify.formatMoney first (Dawn-style themes)
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        const format = window.themeHDN?.settings?.currencyFormat || '${{amount}}';
        return Shopify.formatMoney(cents, format);
      }

      // Try theme's formatMoney if available
      if (typeof window.formatMoney === 'function') {
        const format = window.themeHDN?.settings?.currencyFormat || '${{amount}}';
        return window.formatMoney(cents, format);
      }

      // Use Intl.NumberFormat with Shopify.currency info
      if (typeof Shopify !== 'undefined' && Shopify.currency?.active) {
        try {
          return new Intl.NumberFormat(Shopify.locale || 'en-US', {
            style: 'currency',
            currency: Shopify.currency.active
          }).format(cents / 100);
        } catch (e) {
          console.warn('ViafidoCustomizer: Intl.NumberFormat failed', e);
        }
      }

      // Ultimate fallback - simple formatting
      const amount = (cents / 100).toFixed(2);
      return `$${amount}`;
    }

    // ========== Upload Handling ==========

    extractPetCount(value) {
      const valueMap = { 'one': 1, 'two': 2, 'three': 3, 'four': 4 };
      const lower = value.toLowerCase();
      if (valueMap[lower]) return valueMap[lower];
      const match = value.match(/(\d+)/);
      return match ? parseInt(match[1], 10) : 1;
    }

    updateUploadZones(count) {
      const container = this.elements.uploadContainer;
      if (!container) return;

      container.innerHTML = '';
      this.state.uploads = [];

      // Add instructional header with tips button on right
      const header = document.createElement('div');
      header.className = 'viafido-upload-header';
      header.innerHTML = `
        <div class="viafido-upload-header-row">
          <div class="viafido-upload-header-text">
            <h3 class="viafido-upload-title">Upload Your Pet's Picture</h3>
            <p class="viafido-upload-subtitle">Need help? DM us <a href="https://instagram.com/viafido" target="_blank">@viafido</a> on Instagram or email <a href="mailto:orders@viafido.com">orders@viafido.com</a></p>
          </div>
          <button type="button" class="viafido-tips-btn">📸 Photo Tips</button>
        </div>
      `;
      container.appendChild(header);

      // Create zone container for layout
      const zonesWrapper = document.createElement('div');
      zonesWrapper.className = 'viafido-upload-zones';
      container.appendChild(zonesWrapper);

      for (let i = 0; i < count; i++) {
        const zone = this.createUploadZone(i, count);
        zonesWrapper.appendChild(zone);
        this.state.uploads.push({ index: i, file: null, url: null, name: '' });
      }

      // Create modal (append to body)
      const modal = document.createElement('div');
      modal.className = 'viafido-tips-modal';
      modal.innerHTML = `
        <div class="viafido-tips-modal-overlay"></div>
        <div class="viafido-tips-modal-content">
          <button type="button" class="viafido-tips-modal-close">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
          <h3>📸 Tips for Best Photo</h3>
          <ul>
            <li>Pet facing camera</li>
            <li>Face clearly visible</li>
            <li>Good lighting is a plus</li>
          </ul>
          <p class="viafido-tips-modal-note">We'll reach out if we need an alternate picture.</p>
        </div>
      `;
      document.body.appendChild(modal);

      // Bind modal events
      const openBtn = header.querySelector('.viafido-tips-btn');
      const closeBtn = modal.querySelector('.viafido-tips-modal-close');
      const overlay = modal.querySelector('.viafido-tips-modal-overlay');

      const openModal = () => modal.classList.add('open');
      const closeModal = () => modal.classList.remove('open');

      openBtn?.addEventListener('click', openModal);
      closeBtn?.addEventListener('click', closeModal);
      overlay?.addEventListener('click', closeModal);

      this.updateWizardNextButton();
    }

    createUploadZone(index, totalCount) {
      const petNumber = index + 1;
      const petLabel = totalCount === 1 ? '' : ` ${petNumber}`;

      const zone = document.createElement('div');
      zone.className = 'viafido-upload-zone';
      zone.dataset.index = index;
      zone.innerHTML = `
        <div class="viafido-upload-dropzone">
          <input type="file" accept="image/*" class="viafido-upload-input" data-index="${index}">
          <div class="viafido-upload-placeholder">
            <span class="viafido-upload-icon"><svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M14.2647 15.9377L12.5473 14.2346C11.758 13.4519 11.3633 13.0605 10.9089 12.9137C10.5092 12.7845 10.079 12.7845 9.67922 12.9137C9.22485 13.0605 8.83017 13.4519 8.04082 14.2346L4.04193 18.2622M14.2647 15.9377L14.606 15.5991C15.412 14.7999 15.8149 14.4003 16.2773 14.2545C16.6839 14.1262 17.1208 14.1312 17.5244 14.2688C17.9832 14.4253 18.3769 14.834 19.1642 15.6515L20 16.5001M14.2647 15.9377L18.22 19.9628M18.22 19.9628C17.8703 20 17.4213 20 16.8 20H7.2C6.07989 20 5.51984 20 5.09202 19.782C4.7157 19.5903 4.40973 19.2843 4.21799 18.908C4.12583 18.7271 4.07264 18.5226 4.04193 18.2622M18.22 19.9628C18.5007 19.9329 18.7175 19.8791 18.908 19.782C19.2843 19.5903 19.5903 19.2843 19.782 18.908C20 18.4802 20 17.9201 20 16.8V13M11 4H7.2C6.07989 4 5.51984 4 5.09202 4.21799C4.7157 4.40973 4.40973 4.71569 4.21799 5.09202C4 5.51984 4 6.0799 4 7.2V16.8C4 17.4466 4 17.9066 4.04193 18.2622M18 9V6M18 6V3M18 6H21M18 6H15" stroke="#666" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
            <span class="viafido-upload-text">Pet${petLabel} Photo&nbsp;<span style="color: red;">*</span><br><small>Tap or drop image here</small></span>
          </div>
          <div class="viafido-upload-preview" hidden>
            <img class="viafido-upload-image" src="" alt="">
            <div class="viafido-upload-success-overlay"></div>
            <div class="viafido-upload-info-bar">
              <span class="viafido-upload-status"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> Success</span>
              <span class="viafido-upload-file-info"></span>
              <button type="button" class="viafido-upload-delete" title="Remove">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
          </div>
          <div class="viafido-upload-loading" hidden>
            <div class="viafido-upload-spinner"></div>
          </div>
        </div>
        <input type="text" class="viafido-pet-name" placeholder="Pet${petLabel} Name (optional)" data-index="${index}">
      `;

      this.bindUploadZoneEvents(zone);
      return zone;
    }

    bindUploadZoneEvents(zone) {
      const input = zone.querySelector('.viafido-upload-input');
      const dropzone = zone.querySelector('.viafido-upload-dropzone');

      input.addEventListener('change', (e) => this.handleFileSelect(e, zone));

      dropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        dropzone.classList.add('viafido-dragover');
      });

      dropzone.addEventListener('dragleave', () => {
        dropzone.classList.remove('viafido-dragover');
      });

      dropzone.addEventListener('drop', (e) => {
        e.preventDefault();
        dropzone.classList.remove('viafido-dragover');
        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) {
          this.uploadFile(file, zone);
        }
      });

      // Delete button handler
      const deleteBtn = zone.querySelector('.viafido-upload-delete');
      if (deleteBtn) {
        deleteBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.clearUpload(zone);
        });
      }

      const nameInput = zone.querySelector('.viafido-pet-name');
      nameInput.addEventListener('input', (e) => {
        const index = parseInt(zone.dataset.index, 10);
        if (this.state.uploads[index]) {
          this.state.uploads[index].name = e.target.value;
        }
      });
    }

    handleFileSelect(e, zone) {
      const file = e.target.files[0];
      if (file) {
        this.uploadFile(file, zone);
      }
    }

    async uploadFile(file, zone) {
      const index = parseInt(zone.dataset.index, 10);
      const loading = zone.querySelector('.viafido-upload-loading');
      const preview = zone.querySelector('.viafido-upload-preview');
      const placeholder = zone.querySelector('.viafido-upload-placeholder');
      const image = zone.querySelector('.viafido-upload-image');

      loading.hidden = false;
      placeholder.hidden = true;
      this.updateWizardNextButton();

      try {
        const reader = new FileReader();
        reader.onload = (e) => {
          image.src = e.target.result;
        };
        reader.readAsDataURL(file);

        // Simulated upload with 1.5s delay
        const uploadUrl = await this.performUpload(file);

        if (this.state.uploads[index]) {
          this.state.uploads[index].file = file;
          this.state.uploads[index].url = uploadUrl;
        }

        loading.hidden = true;
        preview.hidden = false;
        zone.classList.add('viafido-upload-complete');

        // Populate file info
        const fileInfo = zone.querySelector('.viafido-upload-file-info');
        if (fileInfo) {
          const size = this.formatFileSize(file.size);
          fileInfo.textContent = `${file.name} (${size})`;
        }

      } catch (error) {
        console.error('Upload failed:', error);
        loading.hidden = true;
        placeholder.hidden = false;
        zone.classList.add('viafido-upload-error');
      }

      this.updateWizardNextButton();
      this.syncPropertiesToForm();
      this.updateBuyButton(this.state.referenceVariant);
    }

    async performUpload(file) {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('https://app.suite.viafido.com/api/store/upload', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Upload failed');
      }

      return data.url;
    }

    clearUpload(zone) {
      const index = parseInt(zone.dataset.index, 10);
      const preview = zone.querySelector('.viafido-upload-preview');
      const placeholder = zone.querySelector('.viafido-upload-placeholder');
      const image = zone.querySelector('.viafido-upload-image');
      const input = zone.querySelector('.viafido-upload-input');
      const fileInfo = zone.querySelector('.viafido-upload-file-info');

      // Delete from server if URL exists
      const uploadData = this.state.uploads[index];
      if (uploadData?.url) {
        fetch('https://app.suite.viafido.com/api/store/upload/delete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: uploadData.url })
        }).catch(err => console.warn('Delete failed:', err));
      }

      // Reset state
      if (this.state.uploads[index]) {
        this.state.uploads[index].file = null;
        this.state.uploads[index].url = null;
      }

      // Reset UI
      preview.hidden = true;
      placeholder.hidden = false;
      image.src = '';
      input.value = '';
      if (fileInfo) fileInfo.textContent = '';
      zone.classList.remove('viafido-upload-complete', 'viafido-upload-error');

      this.updateWizardNextButton();
      this.syncPropertiesToForm();
      this.updateBuyButton(this.state.referenceVariant);
    }

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    }

    areUploadsComplete() {
      if (this.state.uploads.length === 0) return false;
      return this.state.uploads.every(upload => upload.url !== null);
    }

    updateWizardNextButton() {
      // Update our custom wizard next button state
      if (this.elements.nextBtn && this.state.currentStep === 1) {
        const uploadsComplete = this.areUploadsComplete();
        this.elements.nextBtn.disabled = !uploadsComplete;
        this.elements.nextBtn.classList.toggle('hidden', false); // Always show the button
        // Update button text based on upload status
        this.elements.nextBtn.textContent = uploadsComplete ? 'Continue to Customize →' : 'Upload to Continue →';
      }
    }

    getUploadMetadata() {
      const properties = {};
      this.state.uploads.forEach((upload, index) => {
        const petNum = index + 1;
        if (upload.url) {
          properties[`_Pet ${petNum} Image`] = upload.url;
          properties[`Pet ${petNum} Image`] = upload.url;
        }
        if (upload.name) {
          properties[`Pet ${petNum} Name`] = upload.name;
        }
      });
      return properties;
    }

    // ========== Cart Override ==========

    handleFormSubmit(e) {
      if (!this.state.referenceVariant) return;

      e.preventDefault();

      const variantId = this.state.referenceVariant.id;
      const properties = this.getUploadMetadata();

      // Add artist choice property if they selected "Let artist decide" for background
      if (this.state.artistChoosesBackground) {
        properties['Background Color'] = 'Artist will choose best one';
      }


      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: properties
        })
      })
        .then(response => response.json())
        .catch(error => {
          console.error('Add to cart failed:', error);
          const errorContainer = this.elements.form?.querySelector('.hdt-error-message');
          if (errorContainer) {
            errorContainer.textContent = 'Failed to add to cart. Please try again.';
            errorContainer.parentElement.hidden = false;
          }
        });
    }

    // ========== UI Helpers ==========

    showLoading() {
      this.elements.wizardContainer?.classList.add('viafido-loading');
    }

    hideLoading() {
      this.elements.wizardContainer?.classList.remove('viafido-loading');
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  // Export to window
  window.ViafidoCustomizer = ViafidoCustomizer;
})();
